import { useMemo } from 'react';

import { useAppConfig } from '@/hooks/useAppConfig';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import {
  hasHandedOffToOnboardingSpa,
  markOnboardingSpaHandoff,
} from '@/modules/onboarding/utils/crossAppHandoff';
import { resolveOnboardingFlow } from '@/modules/onboarding/utils/onboardingFlow';
import authRoutes from '@/pages/auth/routes';
import workeffyRoutes, { isWorkeffyPath } from '@/pages/corporate/routes/workeffy';

/**
 * The single place that decides where an unfinished user is sent for onboarding.
 *
 * Flag precedence, highest first:
 *  - `isNewOnboardingEnabled` -> the workeffy SPA at `/w/onboarding/<persona>`
 *  - `isUserOnboardingAiEnabled` -> `/verify-yourself`
 *  - neither -> `/about-yourself`
 *
 * The decision used to be copy-pasted across the global gate, the auth page, signup, and
 * three verification pages, and had already drifted (`useSocialAuth` never grew the AI arm).
 * Every one of those call sites should read this hook instead of re-deriving the ternary.
 *
 * Crossing into `/w` is a full page load — `Link`, `Redirect` and `useNavigation` all handle
 * that — so callers must gate on `canEnterOnboarding` and call `markHandoff()` immediately
 * before navigating.
 */
const useOnboardingRoute = () => {
  const { site } = useAppConfig();
  const user = useUserContext();

  const isNewOnboarding = Boolean(site.isNewOnboardingEnabled);
  const isAiOnboarding = Boolean(site.isUserOnboardingAiEnabled);
  const { isStaff } = user;

  return useMemo(() => {
    const onboardingRoute = isNewOnboarding
      ? workeffyRoutes.WorkeffyOnboarding(resolveOnboardingFlow({ isStaff }))
      : isAiOnboarding
      ? authRoutes.VerifyYourself()
      : authRoutes.AboutYourself();

    const isCrossAppOnboarding = isWorkeffyPath(onboardingRoute);

    return {
      isNewOnboarding,
      onboardingRoute,
      isCrossAppOnboarding,
      /** Email verification stays in academy under every flag; only the UI generation differs. */
      verifyEmailRoute:
        isNewOnboarding || isAiOnboarding ? authRoutes.VerifyEmailV2() : authRoutes.VerifyEmail(),
      canEnterOnboarding: !isCrossAppOnboarding || !hasHandedOffToOnboardingSpa(),
      markHandoff: () => {
        if (isCrossAppOnboarding) markOnboardingSpaHandoff();
      },
    };
  }, [isAiOnboarding, isNewOnboarding, isStaff]);
};

export default useOnboardingRoute;
